import Dexie from "dexie"
import { useLiveQuery } from "dexie-react-hooks"
import type { WhatsappMessage } from "@stockline/shared/types/communications/whatsapp-message"
import { offlineDb } from "../../../lib/offline/db"
import { WHATSAPP_MESSAGE_TABLE } from "../../../lib/offline/resources/communications/whatsapp-message/db"

const EMPTY: WhatsappMessage[] = []

/**
 * Reactive read of WhatsApp messages for a single chat, capped at `limit`,
 * ordered **descending** by timestamp (newest → oldest in the array).
 *
 * Uses the `[chatUid+timestamp]` compound index to limit at the IndexedDB
 * level — walks the index high → low and takes only `limit` rows. No
 * full-table scan, no load-all-then-slice.
 *
 * Display ordering:
 * - With `reversed=false` (default `MessageList`, e.g. `/chat` page):
 *   array order = top-to-bottom → newest at the top, oldest at the bottom.
 * - With `reversed=true` (e.g. `/recap` chat panel):
 *   `flex-col-reverse` flips it → newest at the bottom, oldest at the top.
 */
export function useWhatsappMessagesPaged(
  chatUid: string | null,
  limit: number
): WhatsappMessage[] {
  const result = useLiveQuery(
    async () => {
      if (!chatUid) return EMPTY
      const rows = await offlineDb
        .table(WHATSAPP_MESSAGE_TABLE)
        .where("[chatUid+timestamp]")
        .between([chatUid, Dexie.minKey], [chatUid, Dexie.maxKey])
        .reverse()
        .limit(limit)
        .toArray()
      return rows as WhatsappMessage[]
    },
    [chatUid, limit],
    EMPTY
  )
  return result ?? EMPTY
}
